import { useMemo } from 'react';

import { Analysis, AnalysisDisplayCategory, AnalysisDisplayStatus } from 'types';

export const useFilteredAnalysesByStatus = (
  analyses: Analysis[],
  namesToInclude: string[]
): {
  defaultAnalysis: string;
  interesting: Analysis[];
  passed: Analysis[];
  failed: Analysis[];
  running: Analysis[];
  skipped: Analysis[];
  categorizedAnalyses: Analysis[];
} =>
  useMemo(() => {
    const filteredAnalyses = analyses
      .filter((a) => namesToInclude.includes(a.name))
      .sort((a, b) => {
        const isInterestingA = Boolean(a.result?.interesting);
        const isInterestingB = Boolean(b.result?.interesting);
        const failedResultA = a.result === undefined || !a.result.successful;
        const failedResultB = a.result === undefined || !b.result.successful;
        const AOrder = failedResultA ? 2 : isInterestingA ? 3 : 1;
        const BOrder = failedResultB ? 2 : isInterestingB ? 3 : 1;

        if (AOrder > BOrder) {
          return -1;
        } else if (AOrder < BOrder) {
          return 1;
        }

        return 0;
      });

    const defaultAnalysis = filteredAnalyses[0]?.id ?? '';
    const interesting = filteredAnalyses.filter((a) => a.status !== 'running' && a.result?.interesting);
    const passed = filteredAnalyses.filter(
      (a) => a.status !== 'running' && a.result?.successful && !a.result?.interesting
    );
    const failed = filteredAnalyses.filter(
      (a) => a.status !== 'running' && a.status !== 'skipped' && !a.result?.successful
    );
    const running = filteredAnalyses.filter((a) => a.status === 'running');
    const skipped = filteredAnalyses.filter((a) => a.status === 'skipped');

    // define list of analyses for each category
    const applicationChecks: string[] = [
      'ErrorPatternLogs',
      'ErrorSpans',
      'HTTPErrorSeries',
      'SlowRequests',
      'RecentDeployments',
    ];
    const infrastructureChecks: string[] = ['NoisyNeighbors', 'KubeCrashes', 'ResourceContentions'];
    const otherChecks: string[] = [
      'StatusPageOutages',
      'RelatedAlerts',
      'CorrelatedSeries',
      'ServiceLevelExplanations',
    ];
    const customChecks: string[] = ['MetricQuery', 'LogQuery'];

    // this takes the filtered analyses and adds category and status to them
    // this is used to display the analyses in the UI by category instead of by status
    const categorizedAnalyses: Analysis[] = filteredAnalyses.map((a) => {
      const displayCategory: AnalysisDisplayCategory = applicationChecks.includes(a.name)
        ? 'application'
        : infrastructureChecks.includes(a.name)
          ? 'infrastructure'
          : otherChecks.includes(a.name)
            ? 'other'
            : customChecks.includes(a.name)
              ? 'custom'
              : 'internal';

      const displayStatus: AnalysisDisplayStatus =
        a.status !== 'running' && a.result?.interesting
          ? 'interesting'
          : a.status !== 'running' && a.result?.successful && !a.result?.interesting
            ? 'passed'
            : a.status !== 'running' && a.status !== 'skipped' && !a.result?.successful
              ? 'failed'
              : a.status === 'running'
                ? 'running'
                : a.status === 'skipped'
                  ? 'skipped'
                  : 'unknown';

      return { ...a, displayCategory, displayStatus };
    });

    return {
      defaultAnalysis,
      interesting,
      passed,
      failed,
      running,
      skipped,
      categorizedAnalyses,
    };
  }, [analyses, namesToInclude]);
